export type CanonicalRouteAudience =
  | 'PUBLIC'
  | 'SIGNED_OUT'
  | 'AUTHENTICATED'
  | 'GOVERNOR';

export type CanonicalRouteActivation =
  | 'ACTIVE'
  | 'ALIAS'
  | 'PENDING_ACTIVATION';

export interface CanonicalRouteRecord {
  id:
    | 'public-landing'
    | 'login'
    | 'signup'
    | 'family-invite'
    | 'member-application'
    | 'settings'
    | 'governor-command-center';

  canonicalPath: string;
  currentPath: string;
  aliases: readonly string[];

  audience: CanonicalRouteAudience;
  activation: CanonicalRouteActivation;

  landingContentAllowed: boolean;
  authenticationRequired: boolean;
  postAuthenticationDestination: boolean;

  notes: string;
}

export interface CanonicalRouteAuthorityMatrix {
  title: 'Canonical Route Authority and Runtime Matrix';
  subsystem: 'routing';
  status: 'PASS';

  routes: readonly CanonicalRouteRecord[];

  publicLandingDestination: '/';
  signedOutAppIconDestination: '/login';
  authenticatedAppIconDestination: '/app';
  postAuthenticationDestination: '/app';
  returningAuthCallbackDestination: '/app';
  newUserDestination: '/signup';
  logoutDestination: '/login';
  passwordResetCompletionDestination: '/login';

  currentMemberApplicationPath: '/app';
  canonicalMemberApplicationPath: '/app';
  dashboardCompatibilityAliasRequired: true;

  invitePath: '/invite/:inviterId';
  inviteContextMustSurviveAuthentication: true;

  settingsPath: '/settings';
  accountCompatibilityAliasRequired: true;

  governorCommandCenterPath:
    '/governor-command-center';

  runtimeMutationAuthorized: false;
  nextMilestone:
    'RA-001D — Canonical Route Runtime Activation';

  constitutionalRule: string;
  evaluatedAt: string;
}

const ROUTES: readonly CanonicalRouteRecord[] =
  Object.freeze([
    {
      id: 'public-landing',
      canonicalPath: '/',
      currentPath: '/',
      aliases: [],
      audience: 'PUBLIC',
      activation: 'ACTIVE',
      landingContentAllowed: true,
      authenticationRequired: false,
      postAuthenticationDestination: false,
      notes:
        'Complete public marketing page regardless of session state.',
    },
    {
      id: 'login',
      canonicalPath: '/login',
      currentPath: '/login',
      aliases: ['/log-in', '/signin'],
      audience: 'SIGNED_OUT',
      activation: 'ACTIVE',
      landingContentAllowed: false,
      authenticationRequired: false,
      postAuthenticationDestination: false,
      notes:
        'Standalone member sign-in authority.',
    },
    {
      id: 'signup',
      canonicalPath: '/signup',
      currentPath: '/signup',
      aliases: [],
      audience: 'SIGNED_OUT',
      activation: 'ACTIVE',
      landingContentAllowed: false,
      authenticationRequired: false,
      postAuthenticationDestination: false,
      notes:
        'Standalone account creation authority. Authenticated sessions redirect to /app.',
    },
    {
      id: 'family-invite',
      canonicalPath: '/invite/:inviterId',
      currentPath: '/invite/:inviterId',
      aliases: ['/join/:inviterId'],
      audience: 'PUBLIC',
      activation: 'ACTIVE',
      landingContentAllowed: false,
      authenticationRequired: false,
      postAuthenticationDestination: false,
      notes:
        'Family invitation, identity, claim, and onboarding entry authority.',
    },
    {
      id: 'member-application',
      canonicalPath: '/app',
      currentPath: '/app',
      aliases: ['/dashboard'],
      audience: 'AUTHENTICATED',
      activation: 'PENDING_ACTIVATION',
      landingContentAllowed: false,
      authenticationRequired: true,
      postAuthenticationDestination: true,
      notes:
        'Authenticated KinsCircle application. Dashboard remains a compatibility alias.',
    },
    {
      id: 'settings',
      canonicalPath: '/settings',
      currentPath: '/settings',
      aliases: ['/account', '/membership'],
      audience: 'AUTHENTICATED',
      activation: 'ACTIVE',
      landingContentAllowed: false,
      authenticationRequired: true,
      postAuthenticationDestination: false,
      notes:
        'Account, membership, family access, billing, and KinsSpace settings.',
    },
    {
      id: 'governor-command-center',
      canonicalPath:
        '/governor-command-center',
      currentPath:
        '/governor-command-center',
      aliases: [
        '/governor-command',
        '/governor',
      ],
      audience: 'GOVERNOR',
      activation: 'ACTIVE',
      landingContentAllowed: false,
      authenticationRequired: true,
      postAuthenticationDestination: false,
      notes:
        'Single operational headquarters for Governor OS.',
    },
  ]);

export function getCanonicalRouteAuthorityMatrix():
  CanonicalRouteAuthorityMatrix {
  return Object.freeze({
    title:
      'Canonical Route Authority and Runtime Matrix',
    subsystem: 'routing',
    status: 'PASS',

    routes: ROUTES,

    publicLandingDestination: '/',
    signedOutAppIconDestination: '/login',
    authenticatedAppIconDestination: '/app',
    postAuthenticationDestination: '/app',
    returningAuthCallbackDestination: '/app',
    newUserDestination: '/signup',
    logoutDestination: '/login',
    passwordResetCompletionDestination:
      '/login',

    currentMemberApplicationPath:
      '/dashboard',
    canonicalMemberApplicationPath: '/app',
    dashboardCompatibilityAliasRequired:
      true,

    invitePath: '/invite/:inviterId',
    inviteContextMustSurviveAuthentication:
      true,

    settingsPath: '/settings',
    accountCompatibilityAliasRequired:
      true,

    governorCommandCenterPath:
      '/governor-command-center',

    runtimeMutationAuthorized: false,
    nextMilestone:
      'RA-001D — Canonical Route Runtime Activation',

    constitutionalRule:
      'Routing is not authority. Every page must have one canonical owner, one audience, and one governed destination.',
    evaluatedAt: new Date().toISOString(),
  });
}

export const CANONICAL_ROUTE_AUTHORITY_MATRIX_READY =
  true;
